import type { Academy } from '@mathflat/domain/Academy'
import type { Student } from '@mathflat/domain/Student'
import { format } from 'date-fns'

// API 계약상 'yyyy-MM-dd' 형식 보장 — 형식 검사 불필요 (잘못된 값은 NaN으로 즉시 노출됨)
const toZonedISO = (dateStr: string | null | undefined) => {
  if (!dateStr) return dateStr
  const [year, month, day] = dateStr.split('-').map(Number)
  return format(new Date(year, month - 1, day), "yyyy-MM-dd'T'HH:mm:ssxxx")
}

import { convertAcademyToUserProperties, type UserPropertiesSchema } from './userProperties.schema'

// Academy 속성에 academy_ 접두사를 붙인 타입
type PrefixedAcademyProperties = {
  [K in keyof UserPropertiesSchema as `academy_${K}`]: UserPropertiesSchema[K]
}

// 저장소는 로그인 직후 id 만 있는 학생을 들고 있다가 상세를 나중에 채운다. id 외에는 없을 수 있다.
type PrefixedStudentProperties = {
  student_I코드: Student['id']
  student_S코드: Student['loginId'] | undefined
  student_학생명: Student['name'] | undefined
  student_학년: Student['grade'] | undefined
  student_재원상태: Student['status'] | undefined
  student_연락처: Student['phone'] | undefined
  student_학교명: Student['schoolName'] | undefined
  student_생년월일: Student['birthDate'] | undefined
  student_학생이메일: Student['email'] | undefined
}

type PrefixedParentProperties = {
  parent_P코드: NonNullable<Student['parent']>['id'] | undefined
}

export type GlobalEventPropertiesSchema = PrefixedStudentProperties &
  PrefixedParentProperties &
  PrefixedAcademyProperties & {
    globalPropertiesVersion: 'v1.0'
    globalPropertiesUpdateDate: '2025-10-15'
  }

export const convertAcademyAndStudentToGlobalEventProperty = ({
  academy,
  student,
}: {
  academy: Academy
  student: Partial<Student> & { id: Student['id'] }
}): Partial<GlobalEventPropertiesSchema> => {
  const academyProperties = convertAcademyToUserProperties({ academy })
  const prefixedAcademyProperties = Object.entries(academyProperties).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [`academy_${key}`]: value,
    }),
    {} as PrefixedAcademyProperties,
  )
  return {
    ...prefixedAcademyProperties,
    student_I코드: student.id,
    student_S코드: student.loginId,
    student_학생명: student.name,
    student_학년: student.grade,
    student_재원상태: student.status,
    student_연락처: student.phone,
    student_학교명: student.schoolName,
    student_생년월일: toZonedISO(student.birthDate),
    student_학생이메일: student.email,
    parent_P코드: student.parent?.id,
  }
}
